/**
 * DIE REGEL DER GEGENPRÜFUNG, OHNE JSX (.391), damit ein Prüfstand sie laden
 * und rechnen kann statt nur ihren Text zu suchen. Der Block selbst steht in
 * `Rechnungspruefung.tsx`.
 */
export const GEBUEHR_OZ = '10099980';
/** .567: die Energiekostenzulage ist Vertrag MDS–Telekom und keine Leistung einer Firma. */
export const ZULAGE_OZ = ['10081836'];
const nichtFirma = (o: string) => o === GEBUEHR_OZ || ZULAGE_OZ.includes(o);

/**
 * ZEILE FÜR ZEILE (.571). ORIGINALTEXT MARVIN (01.11.2026, RE1295): „Ich hatte
 * wie oft gesagt, dass du die Sachen als einzelne Positionen schreiben sollst.
 * ... Du sollst keine Positionen zusammenfassen."
 *
 * Bis .570 stand je Ordnungszahl EINE Zeile mit Summen (Oberfläche: Aufmaß 13,
 * gutgeschrieben 15 aus 11 + 2 + 2, Rechnung 2, weil der Leser die zweite
 * Zeile nicht las) und daraus „11 statt 2". Jetzt ist jede Zeile der Gutschrift,
 * des Aufmaßes, des LEB und der Rechnung eine eigene Zeile: zuerst werden
 * gleiche Mengen derselben Ordnungszahl zusammengelegt (die 11 m der Gutschrift
 * zu den 11 m der Rechnung), was übrig bleibt, der Reihe nach.
 */
type Quelle = { oz: string; menge: number; vermerk?: string | null; oz_gedruckt?: string | null; kurztext?: string | null; einheit?: string | null; lv_einheit?: string | null };
export function zeilenSlots(listen: Record<'g' | 'a' | 'e' | 'r', Quelle[]>, vorrang: ('g' | 'a' | 'e' | 'r')[]) {
  const tol = 0.005;
  const frei = Object.fromEntries(Object.entries(listen).map(([k, v]) => [k, v.map((x) => ({ x, weg: false }))])) as Record<string, { x: Quelle; weg: boolean }[]>;
  const slots: Record<string, Quelle | null>[] = [];
  // 1. Gleiche Mengen zusammenlegen, ausgehend von der ersten Quelle mit Zeilen.
  for (const anker of vorrang) {
    for (const it of frei[anker]!) {
      if (it.weg) continue;
      const slot: Record<string, Quelle | null> = { g: null, a: null, e: null, r: null };
      let paar = false;
      for (const k of vorrang) {
        if (k === anker) continue;
        const t = frei[k]!.find((y) => !y.weg && Math.abs(Number(y.x.menge) - Number(it.x.menge)) < tol);
        if (t) { t.weg = true; slot[k] = t.x; paar = true; }
      }
      if (!paar) continue;
      it.weg = true; slot[anker] = it.x; slots.push(slot);
    }
  }
  // 2. Was übrig ist, der Reihe nach nebeneinander.
  const rest = Object.fromEntries(vorrang.map((k) => [k, frei[k]!.filter((y) => !y.weg).map((y) => y.x)])) as Record<string, Quelle[]>;
  const n = Math.max(0, ...vorrang.map((k) => rest[k]!.length));
  for (let i = 0; i < n; i++) slots.push({ g: rest.g?.[i] ?? null, a: rest.a?.[i] ?? null, e: rest.e?.[i] ?? null, r: rest.r?.[i] ?? null });
  return slots;
}

export function gegenpruefungZeilen(d: any) {
  if (Array.isArray(d.gutschrift_zeilen) && Array.isArray(d.aufmass_zeilen)) return gegenpruefungZeilenweise(d);
  return gegenpruefungJeOz(d);
}

function gegenpruefungZeilenweise(d: any) {
  const tol = 0.005;
  const ab: any[] = d.anforderung?.abweichungen ?? [];
  const hatGutschrift = (d.gutschrift_zeilen ?? []).length > 0;
  const montage = d.anforderung?.gewerk_gruppe === 'montage';
  const massstab: 'gutschrift' | 'angefordert' | 'aufmass' = hatGutschrift && !montage ? 'gutschrift' : d.anforderung ? 'angefordert' : 'aufmass';
  const nichtMenge = nichtJeOz(d.anforderung?.nicht_positionen ?? []);
  const q = (l: any[]) => (l ?? []).filter((x: any) => x.oz && !nichtFirma(String(x.oz))).map((x: any) => ({ ...x, oz: String(x.oz).trim(), menge: Number(x.menge ?? 0) }));
  const L = { g: q(d.gutschrift_zeilen), a: q(d.aufmass_zeilen), e: q(d.eingereicht_zeilen ?? []), r: q(d.positionen) };
  const ozs = new Set<string>([...L.g, ...L.a, ...L.e, ...L.r].map((x) => x.oz));
  for (const o of nichtMenge.keys()) if (!nichtFirma(o)) ozs.add(o);
  const lvName = (o: string) => [...L.r, ...L.a, ...L.e, ...L.g].find((x) => x.oz === o && x.kurztext)?.kurztext ?? ab.find((a: any) => a.oz === o)?.bezeichnung ?? '';
  const lvEinheit = (o: string) => [...L.r.map((x: any) => x.lv_einheit ?? x.einheit), ...[...L.a, ...L.e, ...L.g].filter((x) => x.oz === o).map((x: any) => x.einheit)].find(Boolean) ?? null;
  const vorrang: ('g' | 'a' | 'e' | 'r')[] = massstab === 'gutschrift' ? ['g', 'a', 'e', 'r'] : ['a', 'g', 'e', 'r'];
  const aus: any[] = [];
  for (const o of [...ozs].sort()) {
    const nur = (k: 'g' | 'a' | 'e' | 'r') => L[k].filter((x) => x.oz === o);
    const slots = zeilenSlots({ g: nur('g'), a: nur('a'), e: nur('e'), r: nur('r') }, vorrang);
    if (!slots.length) slots.push({ g: null, a: null, e: null, r: null });
    const ozHatRechnung = nur('r').length > 0;
    let abgesagtRest = nichtMenge.get(o) ?? 0;
    const einzig = slots.length === 1;
    slots.forEach((sl, i) => {
      const am = sl.a ? sl.a.menge : null, em = sl.e ? sl.e.menge : null, gs = sl.g ? sl.g.menge : null, pm = sl.r ? sl.r.menge : null;
      const a = einzig ? ab.find((x: any) => x.oz === o) : null;
      const angefordert = !d.anforderung ? null : a ? Number(a.menge_gutschrift ?? 0) : (am ?? 0);
      const sollRoh = massstab === 'gutschrift' ? (gs ?? 0) : massstab === 'angefordert' ? (angefordert ?? 0) : (am ?? 0);
      const abgesagt = Math.min(abgesagtRest, sollRoh); abgesagtRest -= abgesagt;
      const soll = Math.max(0, Math.round((sollRoh - abgesagt) * 1000) / 1000);
      const sollWort = abgesagt > 0 && soll <= tol ? 'deine Position (abgesagt)' : massstab === 'gutschrift' ? 'gutgeschrieben' : massstab === 'angefordert' ? 'angefordert' : 'im Aufmaß';
      let lage: 'gruen' | 'rot' | 'blau' | 'grau' = 'gruen'; let wort = 'passt';
      const ist = pm ?? 0;
      if (pm === null && soll <= tol) { lage = 'grau'; wort = 'nichts'; }
      else if (ist > soll + tol) { lage = 'rot'; wort = soll <= tol ? `nicht ${sollWort}` : `zu viel: ${String(ist).replace('.', ',')} statt ${String(soll).replace('.', ',')}`; }
      else if (ist < soll - tol) { lage = 'blau'; wort = pm === null ? `nicht berechnet, ${String(soll).replace('.', ',')} ${sollWort}` : `weniger als ${sollWort}`; }
      else if (am !== null && ist > am + tol) { lage = 'rot'; wort = 'mehr als sein Aufmaß'; }
      /* GUTGESCHRIEBEN, ABER NIE IN SEINEM AUFMASS: bleibt bei uns, die Firma erfährt davon nichts (Regel vom 14.10.2026). */
      if (lage === 'blau' && pm === null && am === null && ozHatRechnung) wort = `nur gutgeschrieben, nicht in seinem Aufmaß`;
      const diff = pm === null && soll <= tol ? 0 : Math.round((ist - soll) * 1000) / 1000;
      const e = lvEinheit(o);
      const vz = diff > tol ? '+' : diff < -tol ? '−' : '';
      const kurz = Math.abs(diff) <= tol ? 'passt' : `${vz}${String(Math.abs(diff)).replace('.', ',')}${e ? ' ' + e : ''}`;
      const kurzWort = Math.abs(diff) <= tol ? '' : diff > 0 ? `mehr als ${sollWort}` : (lage === 'blau' && pm === null && am === null && ozHatRechnung ? 'nur gutgeschrieben, nicht in seinem Aufmaß' : `weniger als ${sollWort}`);
      const vermerk = sl.a?.vermerk ?? sl.e?.vermerk ?? null;
      const name = lvName(o) + (vermerk ? ` (${vermerk})` : '') + (sl.r?.oz_gedruckt ? `, auf der Rechnung mit OZ ${sl.r.oz_gedruckt}` : '');
      /* VORGEWÄHLT: rot (streichen/kürzen) und eine Leistung, die gar nicht auf der Rechnung steht (aufnehmen). */
      const vorwahl = lage === 'rot' || (lage === 'blau' && pm === null && soll > 0 && !ozHatRechnung);
      aus.push({ key: `${o}#${i}`, oz: o, name, vermerk, einheit: e, vorher: am, jetzt: am, am, em, gs, angefordert, rechnung: pm, pm, soll, massstab, lage, wort, angepasst: null, diff, kurz, kurzWort, abgesagt, vorwahl });
    });
  }
  return aus.filter((z) => (z.pm ?? 0) > 0 || (z.angefordert ?? 0) > 0 || (z.gs ?? 0) > 0 || (z.em ?? 0) > 0 || z.abgesagt > 0 || (z.am ?? 0) > 0);
}

function gegenpruefungJeOz(d: any) {
  const summe = (liste: any[], oz: string) => {
    const x = (liste ?? []).filter((y: any) => y.oz === oz);
    return x.length ? x.reduce((s: number, y: any) => s + Number(y.menge ?? 0), 0) : null;
  };
  const ab: any[] = d.anforderung?.abweichungen ?? [];
  const hatGutschrift = (d.gutgeschrieben ?? []).length > 0;
  /* DIE MONTAGE RECHNET ÜBER STANDARDPOSITIONEN (.365): ihre Gutschrift ist
     kein Massstab für ihre Rechnung, dort gilt das Angeforderte. */
  const montage = d.anforderung?.gewerk_gruppe === 'montage';
  const oz = new Set<string>();
  for (const l of [d.positionen, d.aufmass_firma, d.eingereicht, d.gutgeschrieben]) for (const x of l ?? []) if (x.oz && !nichtFirma(x.oz)) oz.add(x.oz);
  for (const a of ab) if (a.oz && !ZULAGE_OZ.includes(a.oz)) oz.add(a.oz);
  /* DER NAME AUS JEDER QUELLE (.408): Rechnung, Aufmaß, Anforderung, LEB,
     Gutschrift. Eine Position, die nur die Telekom gutgeschrieben hat, stand
     sonst ohne Bezeichnung da (RE1307, 10030368). */
  const name = (o: string) => (d.positionen ?? []).find((p: any) => p.oz === o)?.kurztext
    ?? (d.aufmass ?? []).find((p: any) => p.oz === o)?.kurztext ?? ab.find((a: any) => a.oz === o)?.bezeichnung
    ?? (d.eingereicht ?? []).find((p: any) => p.oz === o)?.kurztext
    ?? (d.gutgeschrieben ?? []).find((p: any) => p.oz === o)?.kurztext ?? '';
  const einheit = (o: string) => (d.positionen ?? []).find((p: any) => p.oz === o)?.lv_einheit
    ?? (d.aufmass ?? []).find((p: any) => p.oz === o)?.einheit ?? ab.find((a: any) => a.oz === o)?.einheit
    ?? (d.eingereicht ?? []).find((p: any) => p.oz === o)?.einheit ?? (d.gutgeschrieben ?? []).find((p: any) => p.oz === o)?.einheit ?? null;
  const tol = 0.005;
  /* NICHT DEINE POSITION (.410): was der Firma bei der Freigabe abgesagt wurde
     („10030372 Tiefbau … (Montagegrube 1): 1 St"), zieht vom Maßstab ab.
     Steht es trotzdem auf der Rechnung, ist das rot. */
  const nichtMenge = nichtJeOz(d.anforderung?.nicht_positionen ?? []);
  for (const o of nichtMenge.keys()) if (!nichtFirma(o)) oz.add(o);
  return [...oz].sort().map((o) => {
    const a = ab.find((x: any) => x.oz === o);
    const am = summe(d.aufmass_firma, o);
    const em = summe(d.eingereicht, o);
    const gs = summe(d.gutgeschrieben, o);
    const angefordert = !d.anforderung ? null : a ? Number(a.menge_gutschrift ?? 0) : (am ?? 0);
    const pm = summe(d.positionen, o);
    /* DER MASSSTAB: die Gutschrift, wo es eine mit Positionen gibt (Tiefbau);
       sonst das Angeforderte; ganz ohne Anforderung das Aufmaß der Firma. */
    const massstab: 'gutschrift' | 'angefordert' | 'aufmass' = hatGutschrift && !montage ? 'gutschrift' : angefordert !== null ? 'angefordert' : 'aufmass';
    const abgesagt = nichtMenge.get(o) ?? 0;
    const sollRoh = massstab === 'gutschrift' ? (gs ?? 0) : massstab === 'angefordert' ? (angefordert ?? 0) : (am ?? 0);
    const soll = Math.max(0, Math.round((sollRoh - abgesagt) * 1000) / 1000);
    const sollWort = abgesagt > 0 && soll <= tol ? 'deine Position (abgesagt)' : massstab === 'gutschrift' ? 'gutgeschrieben' : massstab === 'angefordert' ? 'angefordert' : 'im Aufmaß';
    let lage: 'gruen' | 'rot' | 'blau' | 'grau' = 'gruen'; let wort = 'passt';
    const ist = pm ?? 0;
    if (pm === null && soll <= tol) { lage = 'grau'; wort = 'nichts'; }
    else if (ist > soll + tol) { lage = 'rot'; wort = soll <= tol ? `nicht ${sollWort}` : `zu viel: ${ist}`.replace('.', ',') + ` statt ${String(soll).replace('.', ',')}`; }
    else if (ist < soll - tol) { lage = 'blau'; wort = pm === null ? `nicht berechnet, ${String(soll).replace('.', ',')} ${sollWort}` : `weniger als ${sollWort}`; }
    else if (am !== null && ist > am + tol) { lage = 'rot'; wort = 'mehr als sein Aufmaß'; }
    const angepasst = a ? Math.abs((am ?? 0) - Number(a.menge_gutschrift ?? 0)) < tol : null;
    /* DIE DIFFERENZ ALS ZAHL MIT VORZEICHEN (.408). ORIGINALTEXT MARVIN
       (19.10.2026): „wenn du die Menge einfach nur hinschreibst, plus so und
       so viel, minus so und so viel, in Rot und in Grün ... so ist es zu
       unauffällig." Plus = mehr berechnet als der Maßstab (rot), minus =
       weniger (grün, bleibt bei uns), 0 = passt. */
    const diff = pm === null && soll <= tol ? 0 : Math.round(((ist) - soll) * 1000) / 1000;
    const vorzeichen = diff > tol ? '+' : diff < -tol ? '−' : '';
    const kurz = Math.abs(diff) <= tol ? 'passt' : `${vorzeichen}${String(Math.abs(diff)).replace('.', ',')}${einheit(o) ? ' ' + einheit(o) : ''}`;
    const kurzWort = Math.abs(diff) <= tol ? '' : diff > 0 ? `mehr als ${sollWort}` : `weniger als ${sollWort}`;
    return { key: o, vorwahl: lage === 'rot' || (lage === 'blau' && pm === null && soll > 0), vermerk: null, oz: o, name: name(o), einheit: einheit(o), vorher: a ? Number(a.menge_aufmass ?? 0) : am, jetzt: am, am, em, gs, angefordert, rechnung: pm, pm, soll, massstab, lage, wort, angepasst, diff, kurz, kurzWort, abgesagt };
  }).filter((z) => (z.pm ?? 0) > 0 || (z.angefordert ?? 0) > 0 || (z.gs ?? 0) > 0 || (z.em ?? 0) > 0 || z.abgesagt > 0);
}

/** Aus den abgesagten Zeilen („<OZ> Text (Vermerk): <Menge> <Einheit>") die Menge je OZ. */
export function nichtJeOz(zeilen: string[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const t of zeilen ?? []) {
    const oz = String(t).match(/^\s*(\d{6,})/)?.[1];
    if (!oz) continue;
    const menge = String(t).match(/:\s*(-?\d+(?:,\d+)?)\s*[^\d]*$/)?.[1];
    m.set(oz, (m.get(oz) ?? 0) + (menge ? Number(menge.replace(',', '.')) : 0));
  }
  return m;
}


/* DER TEXT DER KORREKTUR, WIE ER RAUSGEHT (.395): dieselben Zeilen wie am
   Server (`abrechnungsfreigabe.ts`, korrekturZeile/korrekturText). Prüfstand
   331 hält beide Fassungen gegeneinander. */
export const RECHNUNG_BITTE =
  'Bitte stell deine Rechnung wie gewohnt an invoice@mds-innovation.com. '
  + 'Auf die Rechnung gehören Baustellenadresse, KLS-ID und SM-Nummer, damit wir sie dem Auftrag zuordnen können.';
const mengeK = (x: number | string | null | undefined, e: string | null) =>
  x === null || x === undefined ? 'nicht aufgeführt' : `${String(Number(x)).replace('.', ',')}${e ? ' ' + e : ''}`;
export function korrekturZeile(p: { oz: string | null; bezeichnung: string | null; einheit: string | null; menge_rechnung: number | null; menge_richtig: number | string }): string {
  const name = [p.oz, p.bezeichnung].filter(Boolean).join(' ');
  const war = p.menge_rechnung == null || Number(p.menge_rechnung) === 0;
  const soll = Number(p.menge_richtig) === 0;
  if (soll && !war) return `   ${name}: streichen (auf der Rechnung ${mengeK(p.menge_rechnung, p.einheit)})`;
  if (war && !soll) return `   ${name}: aufnehmen mit ${mengeK(p.menge_richtig, p.einheit)}`;
  return `   ${name}: ${mengeK(p.menge_richtig, p.einheit)} statt ${mengeK(p.menge_rechnung, p.einheit)}`;
}
export function korrekturText(k: { nr: string; adresse: string | null; kenn: string; punkte: string[]; satz: string | null }): string {
  return [
    `✏️ ${k.nr} bitte neu ausstellen`, '',
    ...(k.adresse ? [`📍 ${k.adresse}${k.kenn}`] : []),
    '   Rechnung bzw. Positionen müssen wie folgt geändert werden:',
    ...k.punkte,
    ...(k.satz ? ['', k.satz] : []),
    '', RECHNUNG_BITTE,
  ].join('\n');
}
